import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell } from "./Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { GraduationCap, Users, BookOpen } from "lucide-react";

const roleOptions = [
  { value: "learner", label: "Learner", icon: BookOpen, desc: "Take courses & tests" },
  { value: "educator", label: "Educator", icon: GraduationCap, desc: "Teach & create courses" },
  { value: "content_seller", label: "Content Seller", icon: Users, desc: "Refer & earn commission" },
];
const dashPath = (role) => ({ educator: "/educator", content_seller: "/seller", learner: "/learner" }[role] || "/learner");

export default function Register() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "", email: "", mobile: "", password: "", confirm: "",
    role: params.get("role") || "learner", referral_code: params.get("ref") || "",
  });
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const register = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) return setError("Passwords do not match");
    if (form.password.length < 6) return setError("Password must be at least 6 characters");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      if (data.dev_otp) toast.info(`Demo OTP: ${data.dev_otp}`, { duration: 10000 });
      toast.success("OTP sent to your email");
      setStep(2);
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const verify = async () => {
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email: form.email, otp });
      login(data.token, data.user);
      toast.success("Account verified!");
      navigate(params.get("next") || dashPath(data.user.role));
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const resend = async () => {
    try { const { data } = await api.post("/auth/resend-otp", { email: form.email }); if (data.dev_otp) toast.info(`Demo OTP: ${data.dev_otp}`, { duration: 10000 }); toast.success("OTP resent"); } catch { toast.error("Failed"); }
  };

  return (
    <AuthShell title={step === 1 ? "Create your account" : "Verify your email"} subtitle={step === 1 ? "Join thousands of learners on VIRIKS" : `Enter the 6-digit code sent to ${form.email}`}>
      {step === 1 ? (
        <form onSubmit={register} className="space-y-4" data-testid="register-form">
          <div className="grid grid-cols-3 gap-2">
            {roleOptions.map((r) => (
              <button type="button" key={r.value} onClick={() => set("role", r.value)} data-testid={`role-${r.value}`}
                className={`p-3 rounded-lg border text-left transition-all ${form.role === r.value ? "border-amber bg-amber/10" : "border-border hover:border-navy/30"}`}>
                <r.icon size={18} className={form.role === r.value ? "text-amber" : "text-navy"} />
                <div className="font-semibold text-xs mt-1">{r.label}</div>
              </button>
            ))}
          </div>
          <div><Label>Full Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="reg-name" required /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="reg-email" required /></div>
          <div><Label>Mobile Number</Label><Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid="reg-mobile" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="reg-password" required /></div>
            <div><Label>Confirm</Label><Input type="password" value={form.confirm} onChange={(e) => set("confirm", e.target.value)} data-testid="reg-confirm" required /></div>
          </div>
          <div><Label>Referral Code (optional)</Label><Input value={form.referral_code} onChange={(e) => set("referral_code", e.target.value)} data-testid="reg-referral" /></div>
          {error && <p className="text-crimson text-sm" data-testid="register-error">{error}</p>}
          <Button type="submit" className="w-full viriks-navy text-white" disabled={loading} data-testid="register-submit">{loading ? "Sending OTP..." : "Continue"}</Button>
          <p className="text-center text-sm text-muted-foreground">Already have an account? <Link to="/login" className="text-amber font-semibold">Login</Link></p>
        </form>
      ) : (
        <div className="space-y-6" data-testid="otp-form">
          <div className="flex justify-center">
            <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
              <InputOTPGroup>
                {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
              </InputOTPGroup>
            </InputOTP>
          </div>
          {error && <p className="text-crimson text-sm text-center" data-testid="otp-error">{error}</p>}
          <Button className="w-full viriks-navy text-white" onClick={verify} disabled={loading || otp.length < 6} data-testid="verify-otp-btn">{loading ? "Verifying..." : "Verify & Continue"}</Button>
          <p className="text-center text-sm text-muted-foreground">Didn't get it? <button onClick={resend} className="text-amber font-semibold">Resend OTP</button></p>
        </div>
      )}
    </AuthShell>
  );
}
